import React from 'react';
import moment from 'moment';
import {
  Drawer,
  Button,
  Row,
  Col,
  message,
  Divider,
  Spin,
  Upload,
  Table,
} from 'antd';
import { CloseOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined, BankOutlined } from '@ant-design/icons';
import { ReceivablesRequestActionStyles } from './Receivables.actionDrawerStyles';
import { StylesProps } from 'src/library/types';
import withStyles from 'react-jss';
import { getS3Url, putImageS3, submitInvoice } from './Receivables.api';
import CommentsAndEventSection from './Receivables.commentAndEventSection';
import { actionTypeAndModalTitleMapping, transactionColumns } from './Receivables.utils';
import { formatDateTime, formatNumber, transformName } from 'src/utils/utils';
import { get } from 'lodash';

const { Dragger } = Upload;

interface IProps extends StylesProps<ReturnType<typeof ReceivablesRequestActionStyles>> {
  isVisible: boolean;
  actionType: string;
  data: any;
  handleClose: (onSuccess?: boolean) => void;
  handleReload: () => void;
  columnsList: any[];
}

const ResubmitInvoiceActionDrawer = (props: IProps): React.ReactElement => {
  const {
    classes,
    data,
    isVisible,
    handleClose,
    handleReload,
    actionType,
    columnsList = [],
  } = props;

  const transactionDetails = data?.transaction_details || [];
  const [isLoading, setIsLoading] = React.useState(false);
  const [hideUploadModal, setHideUploadModal] = React.useState(false);
  const [signedURL, setSignedURL] = React.useState(undefined);
  const [unsignedURL, setUnsignedURL] = React.useState(data?.invoice_pdf_url || '');
  const [fileList, setFileList] = React.useState([]);

  React.useEffect(() => {
    setUnsignedURL(data?.invoice_pdf_url || '');
  }, [data]);

  const uploadPDFCallBack = async (file) => {
    setIsLoading(true);

    const response = await getS3Url({
      invoice_id: data?.id || `INVOICE_${formatDateTime(moment(), 'YYYYMMDDHHmmss')}`,
    });
    if (response.errorMessage) {
      message.error(response.errorMessage);
      setIsLoading(false);
      return;
    }

    const urls = response?.data;
    if (urls?.imageSignedUrl && urls?.imageUrl) {
      const uploadResponse = await putImageS3(urls.imageSignedUrl, file, 'application/pdf');
      if (uploadResponse.errorMessage) {
        message.error(uploadResponse.errorMessage);
        setIsLoading(false);
        setHideUploadModal(true);
        return;
      }
    }
    setSignedURL(urls.imageSignedUrl);
    setUnsignedURL(urls.imageUrl);
    setFileList([file]);
    setIsLoading(false);
  };

  const handleRemoveUpload = () => {
    setSignedURL(undefined);
    setUnsignedURL(undefined);
    setHideUploadModal(false);
    setFileList([]);
  };

  const renderBillingCycle = () => {
    if (data?.start_date && data?.end_date) {
      const startDate = formatDateTime(data.start_date, 'DD MMM YY');
      const endDate = formatDateTime(data.end_date, 'DD MMM YY');
      return `${startDate} - ${endDate}`;
    }
    return '';
  };
  const isValue = (data) => {
    if (Array.isArray(data) || typeof data === 'object') {
      return '-';
    }
    return data !== null && data !== undefined ? data : '-';
  };

  const renderDate = (date) => {
    if (Array.isArray(date) || typeof date === 'object') {
      return '-';
    }
    return formatDateTime(date, 'DD MMM YY', '-');
  };

  const transactionDetailsWithKey = transactionDetails.map((item, idx) => ({
    ...item,
    key: item.transaction_ref_no ? `${item.transaction_ref_no}_${idx}` : `row_${idx}`,
  }));

  const getColumnsToShow = (data: any) => {
    const filteredColumns = (columnsList || []).filter((col) => col.column_id !== 'actions');
    const mappedColumns = filteredColumns.map((col) => {
      let content: React.ReactNode = isValue(data[col.column_id]);
      switch (col.column_id) {
        case 'billing_cycle':
          content = renderBillingCycle();
          break;
        case 'status':
          content = isValue(data.status?.toUpperCase().replace(/_/g, ' '));
          break;
        case 'created_at':
          content = renderDate(data.created_at);
          break;
        case 'net_amount':
        case 'total_tax':
        case 'final_amount':
          content = formatNumber(data[col.column_id]);
          break;
        case 'invoice_number':
          content = get(data, col.column_id, '-') || '-';
          break;
        default:
          content = transformName(get(data, col.column_id, '-') || '-');
          break;
      }
      const style = col.column_id === 'invoice_number'
        ? { minWidth: '250px', width: '250px' }
        : {};
      return {
        hint: col.pretty_name,
        content,
        span: 4,
        style,
      };
    });

    mappedColumns.push({
      hint: 'Supporting Calculations',
      content: (
        <div style={{
          width: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 40,
        }}
        >
          {data?.invoice_computation_excel_url ? (
            <a
              href={data.invoice_computation_excel_url}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
              >
                Download
              </Button>
            </a>
          ) : (
            <span style={{ color: 'grey' }}>-</span>
          )}
        </div>
      ),
      span: 8,
      style: {},
    });

    return mappedColumns;
  };

  const generateColumnForTransactionsLog = () => {
    const columnData: any = [];
    transactionColumns.forEach((item) => {
      const itemToPush = {
        title: item.pretty_name,
        dataIndex: item.column_id,
        key: item.column_id,
        width: item.width || '150px',
      };
      columnData.push(itemToPush);
    });
    return columnData;
  };

  const renderInfoContainer = (hint: any, content: any, key?: string) => {
    const { classes } = props;
    return (
      <Col key={key} span={4} className={classes.infoBox}>
        <div className={classes.hint}>{hint}</div>
        <div className={classes.content}>{content}</div>
      </Col>
    );
  };

  const renderDetails = () => {
    return data ?
      <Row className={classes.divFlex}>
        {getColumnsToShow(data).map((i) => renderInfoContainer(i.hint, i.content, i.hint))}
      </Row>
      : null;
  };

  const handleResubmitInvoice = async () => {
    try {
      if (!unsignedURL) {
        message.error('Please upload invoice');
        return;
      }

      setIsLoading(true);
      const body = {
        id: String(data?.id),
        raised_invoice_pdf_url: unsignedURL,
        action_type: 'resubmit_invoice',
      };
      const response = await submitInvoice(body);
      if (response.isSuccess && response?.data?.success) {
        message.success(response.message);
      } else {
        message.error(response.errorMessage);
      }
    } catch (err) {
      setIsLoading(false);
      message.error(err.message);
      return;
    }

    setIsLoading(false);
    handleClose();
    handleRemoveUpload();
    handleReload();
  };

  const uploadProps = {
    name: 'file',
    accept: '.pdf,',
    multiple: false,
    action: uploadPDFCallBack,
    onRemove: (file) => {
      setSignedURL(undefined);
      setUnsignedURL(undefined);
      setHideUploadModal(false);
      setFileList([]);
    },
  };

  const renderTitle = () => {
    return (
      <div className={classes.drawerTitle}>
        <div className={classes.titleHeading}>
          <CloseOutlined
            onClick={() => handleClose()}
            className={classes.titleClose}
          />
          {actionTypeAndModalTitleMapping[actionType]}
        </div>
        <Button
          style={{ marginLeft: 800 }}
          type="primary"
          onClick={() => handleClose()}
        >
          Cancel
        </Button>
        <Button
          style={{ marginLeft: '4px', marginRight: '16px' }}
          type="primary"
          onClick={() => handleResubmitInvoice()}
        >
          Resubmit
        </Button>
      </div>
    );
  };

  return (
    <Drawer
      title={renderTitle()}
      closable={false}
      onClose={() => handleClose()}
      visible={isVisible}
      width={1200}
    >
      <div>
        <div className={classes.detailsContainer}>
          {renderDetails()}
        </div>
        <Divider />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 1 }}>
          <CommentsAndEventSection
            classes={classes}
            data={data}
          />
        </div>
        <div style={{ borderLeft: '2px solid #f0f0f0', minHeight: '100vh' }} />
        <div style={{ borderLeft: '2px solid #f0f0f0', minHeight: '100vh' }} />
        <div style={{ flex: 1 }}>
          {((!signedURL && !unsignedURL && !hideUploadModal)) ? (
            <Row>
              <Col span={24}>
                <Dragger {...uploadProps} fileList={fileList} className={classes.dragger}>
                  <p>
                    {isLoading ? <Spin size="large" /> : <InboxOutlined style={{ fontSize: '24px' }} />}
                  </p>
                  <p>
                    Click or drag file to this area to upload
                  </p>
                  <p>
                    Please upload PDF files only
                  </p>
                </Dragger>
              </Col>
            </Row>
          ) : (
            <div>
              <DeleteOutlined
                style={{ fontSize: '18px', float: 'right', marginBottom: '10px' }}
                onClick={() => handleRemoveUpload()}
              />
              <iframe
                title="invoice"
                src={unsignedURL}
                width="100%"
                height="80vh"
                style={{ marginLeft: '10px', boxSizing: 'content-box', height: '80vh' }}
              />
            </div>
          )}
        </div>
        {transactionDetails && transactionDetails.length > 0 ? (
          <div style={{ marginTop: 24 }}>
            <div style={{ borderTop: '2px solid #f0f0f0', margin: '8px 0' }} />
            <div style={{ marginLeft: '6px', fontSize: '14px', marginTop: '2px', fontWeight: 500 }}>
              <BankOutlined style={{ marginRight: '4px' }} />
              Transaction Details
            </div>
            <div style={{ borderTop: '2px solid #f0f0f0', margin: '8px 0' }} />
            <Table
              columns={generateColumnForTransactionsLog()}
              dataSource={transactionDetailsWithKey}
              pagination={false}
              size="small"
              bordered
              rowKey="key"
              locale={{ emptyText: 'No transaction details available' }}
            />
          </div>
        ) : null}
      </div>
    </Drawer>
  );
};

export default withStyles(ReceivablesRequestActionStyles)(ResubmitInvoiceActionDrawer);
